/* eslint-disable no-param-reassign */
import Vuex from 'vuex';
import formatter from 'date-fns/format';
import { toZonedTime } from 'date-fns-tz';
import * as Sentry from '@sentry/vue';
import * as FullStory from '@fullstory/browser';
import { debounce } from './utils/debounce';
import detailViewTasks from './modules/detail-view/detail-view-tasks';
import detailViewRecordings from './modules/detail-view/detail-view-recordings';
import detailViewLocations from './modules/detail-view/detail-view-locations';
import detailViewGroups from './modules/detail-view/detail-view-groups';
import detailViewDevices from './modules/detail-view/detail-view-devices';
import detailViewAlarms from './modules/detail-view/detail-view-alarms';
import accountStore from './modules/account';

export default (axios, i18n) => {
  const setApmLoginData = (user, account, license) => {
    if (user === null) {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Clearing Sentry scope user data (User is null / User logging out)',
        level: 'info',
      });
      Sentry.getCurrentScope().setUser(null);
      return;
    }

    if (Sentry && user) {
      Sentry.setUser({
        id: user.id,
        // username: '',
        email: user.email,
        ip_address: '{{auto}}',
      });

      Sentry.setContext('user_data', {
        is_administrator: user.administrator,
      });
    }

    if (account) {
      Sentry.setTag('account_id', account.account_id);
      Sentry.setTag('account_name', account.name);
      Sentry.setTag('account_display', account.account_name);
      Sentry.setTag('license_id', account.license.id);
      Sentry.setTag('license_key', account.license.license);
      Sentry.setContext('account', {
        id: account.id,
        name: account.name,
        company_name: account.company_name,
        owner_id: account.owner_id,
        marketing: account.marketing,
        terms: account.terms,
        time_zone: account.time_zone,
        created_at: account.created_at,
        updated_at: account.updated_at,
      });
    }

    if (license) {
      Sentry.setContext('license', {
        id: license.id,
        // license: license,
        account_id: license.account_id,
        created_at: license.created_at,
        updated_at: license.updated_at,
      });
    }

    if (user && account) {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: `Authenticated user ${user.email} of account ${user.account.name}`,
        level: 'info',
      });
    }
  };

  const validateLicenseData = license => {
    // @TODO validate the license here
    if (!license) {
      // eslint-disable-next-line no-console
      console.error('License invalid!');
    }
  };

  const pingOrLogout = async () => {
    try {
      await axios.get('/ping');
    } catch (e) {
      setApmLoginData(null);
      window.location.href = `${window.app.url}/logout`;
    }
  };

  const authBeat = expiry => {
    setInterval(pingOrLogout, ((expiry * 60) * 1000) + 2000);
  };

  const ping = debounce(5000, pingOrLogout);

  const phpToDateFnsFormat = format => {
    const tokens = {
      A: 'a',
      d: 'dd',
      D: 'EEE',
      l: 'EEEE',
      m: 'LL',
      M: 'MMM',
      F: 'MMMM',
      y: 'yy',
      Y: 'yyyy',
      h: 'hh',
      H: 'HH',
      i: 'mm',
      j: 'd',
      s: 'ss',
    };
    // @TODO: Check the following reduce() call is accurate as the signature shows as invalid?
    // noinspection JSCheckFunctionSignatures
    return Object.entries(tokens)
      .reduce(
        // eslint-disable-next-line no-shadow
        (format, [token, replacement]) => format.replaceAll(token, replacement),
        format,
      );
  };

  const store = new Vuex.Store({
    modules: {
      detailViewTasks,
      detailViewRecordings,
      detailViewLocations,
      detailViewGroups,
      detailViewDevices,
      detailViewAlarms,
      account: accountStore,
    },
    state() {
      return {
        app: { ...window.app },
        user: window.user ? { ...window.user } : null,
        originalUser: window.originalUser ? { ...window.originalUser } : null,
        snackbar: {
          active: false,
          color: undefined,
          text: '',
          timeout: 5000,
          btn: {
            color: undefined,
            text: '',
          },
        },
        location: null,
        dashSettings: false,
        dashboard: {
          locations: [],
          groups: [],
          devices: [],
          alarms: 0,
          selectedLocation: null, // Dashboard Filter
          filterActive: false, // Dashboard filter
        },
        states: {},
      };
    },
    mutations: {
      login(state, user) {
        if (state.user !== user) {
          state.user = user;
          try {
            setApmLoginData(user, user.account, user.account.license);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to set APM data during login, traces may be missing context.', e);
          }
        }
        if (user.language !== i18n.locale) {
          // eslint-disable-next-line no-console
          console.log(`[i18n] Switching loaded locale from ${i18n.locale} to ${user.language} for login`);
          i18n.locale = user.language;
        }
        authBeat(user.auth_expiry);
      },
      setStates(state, states) {
        state.states = states;
      },
      setUser(state, user) {
        state.user = user;
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Logged in user data updated, updating Sentry context/tags',
          level: 'info',
        });
        if (state.user !== user) {
          state.user = user;
          try {
            setApmLoginData(user, user.account, user.account.license);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to set APM data during user update, traces may be missing context.', e);
          }
        }
        if (user.language !== i18n.locale) {
          // eslint-disable-next-line no-console
          console.log(`[i18n] Switching loaded locale from ${i18n.locale} to ${user.language} for setUser`);
          i18n.locale = user.language;
        }
      },
      setLicense(state, license) {
        // eslint-disable-next-line no-console
        console.log('Setting license via setLicense', { state, oldLicense: state.user.account.license, newLicense: license }); // @TODO Remove Me
        state.user.account.license = license;
      },
      /**
       * Set the Location for Dashboard elements
       *
       * @param state
       * @param location
       */
      setLocation(state, location) {
        state.location = location;
      },
      /**
       * Set the state of the dashboard filter
       *
       * @param state
       * @param activeFilter
       */
      setDashboardActiveFilter(state, activeFilter) {
        state.dashboard.filterActive = activeFilter;
      },
      setDashSettings(state, dashSettings) {
        state.dashSettings = dashSettings;
      },
      setDashboard(state, dashboard) {
        state.dashboard = dashboard;
      },
    },
    getters: {
      app: state => state.app,
      appName: state => state.app.name,
      locale: state => state.app.locale,
      config: state => state.app.config,
      isLoggedIn: state => state.user !== null,
      // eslint-disable-next-line no-unused-vars
      ping: state => ping,
      user: state => state.user,
      apiToken: state => state.user.api_token,
      isComarkAdmin: state => state.user.comark_admin === true,
      isAdmin: state => state.user.administrator === true,
      language: state => state.user.language,
      dateFormat: state => state.user.date_format,
      permissions: state => state.user.permissions,
      can: state => permission => state.user.comark_admin === true || !!state.user.permissions.find(perm => perm.name === permission),
      account: state => state.user.account,
      license: state => ((state.user && state.user.account && state.user.account.license) ? state.user.account.license : null),
      licenseName: state => {
        if (!state.user || !state.user.account || !state.user.account.license || !state.user.account.license.data) return ''; // Invalid license data
        if (state.user.account.license.data[0] && state.user.account.license.data[0].product_title) return state.user.account.license.data[0].product_title; // Older web licenses
        if (state.user.account.license.data.data && state.user.account.license.data.data.resources && state.user.account.license.data.data.resources.product_title) return state.user.account.license.data.data.resources.product_title; // Newer printed licenses
        if (state.user.account.license.data.data && state.user.account.license.data.data.resources && state.user.account.license.data.data.resources[0] && state.user.account.license.data.data.resources[0].product_title) return state.user.account.license.data.data.resources[0].product_title; // Newer web licenses
        return 'Error'; // Some other data structure?
      },
      licenseLevel: state => (state.user && state.user.account && state.user.account.license && state.user.account.license.level ? state.user.account.license.level : ''),
      getDate: state => date => toZonedTime(date, state.user.account.time_zone),
      formatDate: state => (date, format) => (date ? formatter(toZonedTime(date, state.user.account.time_zone), phpToDateFnsFormat(format || state.user.date_format)) : ''),
      dateFormats: (state, getters) => {
        const today = new Date();
        const date = Date.UTC(today.getFullYear(), today.getMonth(), 28, 14, 12, 30);
        return getters.config.date_formats.map(format => ({ text: getters.formatDate(date, format), value: format }));
      },
      languages: state => state.app.config.languages,
      languagesDisplay: () => {
        if (window && window.app && window.app.config && window.app.config.languages) {
          const { languages } = window.app.config;
          const entries = Object.entries(languages);
          const langs = [];
          // eslint-disable-next-line no-restricted-syntax
          for (const [key, value] of entries) {
            langs.push({
              value: key,
              text: value,
            });
          }
          return langs;
        }

        // In the (odd) event the backend has been misconfigured, fall back to en/English as it is supported in all versions.
        // eslint-disable-next-line no-console
        console.error('[i18n] App Languages not available on registration load, EN fallback will be used!');
        return [{
          text: 'English',
          value: 'en',
        }];
      },
      views: state => state.app.config.views,
      location: state => state.location,
      dashSettings: state => state.dashSettings,
      dashboard: state => state.dashboard,
      states: state => state.states,
      administrator: state => state.user.administrator,
      timeZone: state => state.user.account.time_zone,
      version: () => process.env.MIX_DILIGENCE_FRONTEND_VERSION,
    },
    actions: {
      snackbar({ state }, options = {}) {
        return new Promise(resolve => {
          state.snackbar = {
            color: undefined,
            text: '',
            timeout: 5000,
            ...options,
            btn: {
              color: undefined,
              text: i18n.t('common.dismiss'),
              ...(options.btn || {}),
            },
            active: true,
          };

          setTimeout(() => {
            state.snackbar.active = false;
            resolve();
          }, state.snackbar.timeout);
        });
      },
      error({ dispatch }, options = {}) {
        if (options instanceof Error) {
          const { response = {}, message = '' } = options;
          if (response && response.data && response.data.message && response.data.message !== '') {
            return dispatch('snackbar', { text: response.data.message, color: 'error' });
          } if (message !== '') {
            return dispatch('snackbar', { text: message, color: 'error' });
          }
          return dispatch('snackbar', { text: i18n.t('common.unknown_error'), color: 'error' });
        }
        return dispatch('snackbar', { ...options, color: 'error' });
      },
      success({ dispatch }, options = {}) {
        if (typeof options === 'string') {
          return dispatch('snackbar', { text: options, color: 'success' });
        }
        return dispatch('snackbar', { ...options, color: 'success' });
      },
    },
  });

  axios.interceptors.request.use(config => {
    if (store.getters.isLoggedIn) {
      if (store.getters.apiToken && !config.headers.hasOwnProperty('Authorization') && config.auth !== false) {
        config.headers.Authorization = `Bearer ${store.getters.apiToken}`;
      }
      if (config.url !== '/ping') {
        store.getters.ping();
      }
    }

    if (process.env.MIX_DILIGENCE_FULLSTORY_ENABLED === 'true') {
      config.headers['X-FullStory-URL'] = FullStory.getCurrentSessionURL(true);
    }

    return config;
  }, error => Promise.reject(error));

  if (store.getters.isLoggedIn) {
    authBeat(store.getters.user.auth_expiry, store.getters.user);
    try {
      setApmLoginData(store.getters.user, store.getters.account, store.getters.license);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to set APM data post-login, traces may be missing context.', e);
    }
  }

  // Initial ping to check if the user is still authenticated
  const path = window.location.pathname;
  if (path !== '/logout' && path !== '/login' && path !== '/register' && !path.startsWith('/reset-password/')) {
    pingOrLogout()
      .catch(e => {
        // eslint-disable-next-line no-console
        console.error('pingOrLogout failed:', e);
      });
  }

  // Force logout when a user is unauthenticated (401)
  axios.interceptors.response.use(
    response => {
      if (response.data.user && response.data.user.account.mfa_enabled && response.data.user.mfa_secret === null) {
        const currentPath = window.location.pathname;
        if (!currentPath.startsWith('/profile') && currentPath !== '/logout' && currentPath !== '/login' && currentPath !== '/register') {
          window.location.href = '/profile';
        }
      }
      return response;
    },
    error => {
      if (error.response && error.response.status === 401) {
        // Clear client-side state
        localStorage.clear();
        sessionStorage.clear();

        // Prevent redirect loop on logout page
        const currentPath = window.location.pathname;
        if (currentPath !== '/logout' && currentPath !== '/login') {
          window.location.href = '/logout';
        }
        // Do nothing if already on /logout
      }
      return Promise.reject(error);
    },
  );

  if (store.getters.license) {
    validateLicenseData(store.getters.license);
  }

  return store;
};
